import { useEffect, useMemo, useState } from 'react';
import { DeliveryMap } from './DeliveryMap';
import {
  loadPublicData,
  loadStore,
  submitOrder,
  trackOrder,
  type Catalog,
  type CheckoutPayload,
  type DeliveryArea,
  type Product,
  type Store
} from './api';
import { isPublicOrderingAvailable, STORE_AVAILABILITY_REFRESH_MS } from './availability';
import { cartItemUnitCents, cartSubtotalCents, toCheckoutItems, type CartItem } from './cart';
import {
  accuracyMessage,
  checkoutModeAvailable,
  confirmedLocation,
  deliveryViewport,
  geolocationErrorMessage,
  type ConfirmedLocation
} from './delivery-location';
import { publicOrderStatusLabel } from './presentation';

const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

interface ProductEditor {
  readonly product: Product;
  readonly cartId?: string;
  readonly quantity: number;
  readonly optionQuantities: Readonly<Record<string, number>>;
  readonly removedIngredientIds: readonly string[];
  readonly notes: string;
}

export function App() {
  const [store, setStore] = useState<Store>();
  const [catalog, setCatalog] = useState<Catalog>();
  const [areas, setAreas] = useState<readonly DeliveryArea[]>([]);
  const [loadingError, setLoadingError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>();
  const [editor, setEditor] = useState<ProductEditor>();
  const [cart, setCart] = useState<readonly CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [mode, setMode] = useState<'PICKUP' | 'DELIVERY'>('PICKUP');
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [city, setCity] = useState('');
  const [deliveryAreaId, setDeliveryAreaId] = useState('');
  const [complement, setComplement] = useState('');
  const [reference, setReference] = useState('');
  const [location, setLocation] = useState<ConfirmedLocation>();
  const [mapOpen, setMapOpen] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const [paymentIntent, setPaymentIntent] = useState<CheckoutPayload['paymentIntent']>('PIX');
  const [cashChange, setCashChange] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [confirmation, setConfirmation] = useState<{
    orderId: string;
    orderNumber: number;
    trackingToken: string;
    status: string;
    totalCents: number;
    estimatedMinutes: number;
  }>();

  useEffect(() => {
    void loadPublicData()
      .then((data) => {
        setStore(data.store);
        setCatalog(data.catalog);
        setAreas(data.deliveryAreas);
        setCity((current) => current || data.store.address.city);
        setActiveCategory(data.catalog.categories[0]?.id);
        if (
          !data.store.pickupEnabled &&
          data.store.deliveryEnabled &&
          data.deliveryAreas.length > 0
        )
          setMode('DELIVERY');
      })
      .catch(() =>
        setLoadingError(
          'Pedidos pela internet estão temporariamente indisponíveis. Tente novamente em instantes.'
        )
      );
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadStore()
        .then(setStore)
        .catch(() => undefined);
    }, STORE_AVAILABILITY_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!confirmation) return;
    const timer = window.setInterval(() => {
      void trackOrder(confirmation.orderId, confirmation.trackingToken)
        .then((order) =>
          setConfirmation((current) =>
            current ? { ...current, status: order.status, totalCents: order.totalCents } : current
          )
        )
        .catch(() => undefined);
    }, 20_000);
    return () => window.clearInterval(timer);
  }, [confirmation?.orderId, confirmation?.trackingToken]);

  const visibleProducts = useMemo(() => {
    if (!catalog) return [];
    const term = search.trim().toLocaleLowerCase('pt-BR');
    return catalog.products.filter(
      (product) =>
        (!activeCategory || product.categoryId === activeCategory) &&
        (!term ||
          `${product.name} ${product.description}`.toLocaleLowerCase('pt-BR').includes(term))
    );
  }, [catalog, search, activeCategory]);

  const subtotal = cartSubtotalCents(cart);
  const selectedArea = areas.find((area) => area.id === deliveryAreaId);
  const deliveryAvailable = Boolean(store?.deliveryEnabled && areas.length > 0);
  const modeAvailable = checkoutModeAvailable(
    mode,
    Boolean(store?.pickupEnabled),
    Boolean(store?.deliveryEnabled),
    areas.length
  );
  const estimatedTotal = subtotal + (mode === 'DELIVERY' ? (selectedArea?.feeCents ?? 0) : 0);

  const openProduct = (product: Product, item?: CartItem) => {
    setEditor({
      product,
      ...(item ? { cartId: item.id } : {}),
      quantity: item?.quantity ?? 1,
      optionQuantities: Object.fromEntries(
        item?.options.map((entry) => [entry.option.id, entry.quantity]) ?? []
      ),
      removedIngredientIds: item?.removedIngredientIds ?? [],
      notes: item?.notes ?? ''
    });
  };

  const saveProduct = () => {
    if (!editor || !catalog) return;
    const groups = catalog.groups.filter((group) => group.productId === editor.product.id);
    for (const group of groups) {
      const selected = catalog.options
        .filter((option) => option.groupId === group.id)
        .reduce((total, option) => total + (editor.optionQuantities[option.id] ?? 0), 0);
      if (selected < group.minQuantity || selected > group.maxQuantity) {
        setCheckoutError(
          `${group.name}: escolha entre ${group.minQuantity} e ${group.maxQuantity}.`
        );
        return;
      }
    }
    const item: CartItem = {
      id: editor.cartId ?? crypto.randomUUID(),
      product: editor.product,
      quantity: editor.quantity,
      options: catalog.options
        .filter((option) => (editor.optionQuantities[option.id] ?? 0) > 0)
        .map((option) => ({ option, quantity: editor.optionQuantities[option.id] ?? 0 })),
      removedIngredientIds: editor.removedIngredientIds,
      notes: editor.notes
    };
    setCart((current) =>
      editor.cartId
        ? current.map((entry) => (entry.id === editor.cartId ? item : entry))
        : [...current, item]
    );
    setCheckoutError('');
    setEditor(undefined);
  };

  const locate = () => {
    const geolocation = Reflect.get(navigator, 'geolocation') as Geolocation | undefined;
    if (!geolocation) {
      setLocationMessage('Este navegador não oferece localização. Escolha o ponto no mapa.');
      setMapOpen(true);
      return;
    }
    geolocation.getCurrentPosition(
      (position) => {
        setLocation(
          confirmedLocation(
            position.coords.latitude,
            position.coords.longitude,
            new Date().toISOString(),
            position.coords.accuracy
          )
        );
        setMapOpen(true);
        setLocationMessage(
          accuracyMessage(position.coords.accuracy) ?? 'Localização da entrega confirmada.'
        );
      },
      (error) => {
        setLocationMessage(geolocationErrorMessage(error.code));
        setMapOpen(true);
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
    );
  };

  const mapViewport = deliveryViewport(location, store?.address);

  const checkout = async () => {
    if (!store || cart.length === 0) return;
    if (!isPublicOrderingAvailable(store)) {
      setCheckoutError('O estabelecimento não está aceitando pedidos neste momento.');
      return;
    }
    if (subtotal < store.minimumOrderCents) {
      setCheckoutError(`O pedido mínimo é ${money.format(store.minimumOrderCents / 100)}.`);
      return;
    }
    if (
      mode === 'DELIVERY' &&
      (!location || !selectedArea || !street.trim() || !number.trim() || !city.trim())
    ) {
      setCheckoutError('Confirme endereço, área de entrega e localização no mapa.');
      return;
    }
    setSubmitting(true);
    setCheckoutError('');
    try {
      const cashChangeForCents = cashChange.trim()
        ? Math.round(Number(cashChange.replace(',', '.')) * 100)
        : undefined;
      const result = await submitOrder(
        {
          mode,
          customer: { name: customerName, phone },
          items: toCheckoutItems(cart),
          paymentIntent,
          ...(paymentIntent === 'CASH' && cashChangeForCents !== undefined
            ? { cashChangeForCents }
            : {}),
          ...(orderNotes.trim() ? { notes: orderNotes.trim() } : {}),
          ...(mode === 'DELIVERY' && location && selectedArea
            ? {
                address: {
                  street,
                  number,
                  city,
                  deliveryAreaId: selectedArea.id,
                  ...(complement.trim() ? { complement } : {}),
                  ...(reference.trim() ? { reference } : {}),
                  ...location,
                  confirmedAt: new Date().toISOString()
                }
              }
            : {})
        },
        idempotencyKey
      );
      setConfirmation(result);
      setCart([]);
      setCartOpen(false);
      setIdempotencyKey(crypto.randomUUID());
    } catch (error) {
      setCheckoutError(
        error instanceof Error ? error.message : 'Não foi possível enviar o pedido.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingError)
    return (
      <main className="center-state">
        <h1>Cardápio indisponível</h1>
        <p>{loadingError}</p>
        <button onClick={() => window.location.reload()}>Tentar novamente</button>
      </main>
    );
  if (!store || !catalog)
    return (
      <main className="center-state" aria-busy="true">
        Carregando cardápio…
      </main>
    );
  const orderingAvailable = isPublicOrderingAvailable(store);

  return (
    <>
      <header className="hero">
        <div className="hero-content">
          {store.logoUrl ? <img src={store.logoUrl} alt={`Logo de ${store.tradeName}`} /> : null}
          <div>
            <span className={`status ${orderingAvailable ? 'open' : 'closed'}`}>
              {orderingAvailable ? 'Aberto agora' : 'Fechado'}
            </span>
            <h1>{store.tradeName}</h1>
            {store.message ? <p>{store.message}</p> : null}
            <small>
              {store.address.street}, {store.address.number} · {store.address.neighborhood}
            </small>
          </div>
        </div>
      </header>

      {!store.acceptingOnlineOrders ? (
        <div className="notice" role="status">
          Cardápio disponível para consulta. Pedidos pela internet estão pausados.
        </div>
      ) : null}

      <main className="menu">
        <label className="search">
          <span>Buscar no cardápio</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Ex.: hambúrguer, batata…"
          />
        </label>
        <nav className="categories" aria-label="Categorias do cardápio">
          {catalog.categories.map((category) => (
            <button
              className={activeCategory === category.id ? 'active' : ''}
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </nav>
        <section className="product-grid" aria-live="polite">
          {visibleProducts.map((product) => (
            <article
              className={`product ${product.available ? '' : 'unavailable'}`}
              key={product.id}
            >
              {product.imageUrl ? (
                <img src={product.imageUrl} alt="" loading="lazy" />
              ) : (
                <div className="image-placeholder" />
              )}
              <div className="product-body">
                <h2>{product.name}</h2>
                <p>{product.description}</p>
                <div>
                  <strong>{money.format(product.priceCents / 100)}</strong>
                </div>
                <button
                  disabled={!product.available || !orderingAvailable}
                  onClick={() => openProduct(product)}
                >
                  {product.available ? 'Adicionar' : 'Indisponível'}
                </button>
              </div>
            </article>
          ))}
        </section>
      </main>

      {cart.length ? (
        <button
          className="cart-fab"
          onClick={() => setCartOpen(true)}
          aria-label={`Abrir carrinho com ${cart.length} itens`}
        >
          <span>Carrinho · {cart.length}</span>
          <strong>{money.format(subtotal / 100)}</strong>
        </button>
      ) : null}

      {editor ? (
        <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="product-title">
          <section className="sheet product-editor">
            <button className="close" aria-label="Fechar" onClick={() => setEditor(undefined)}>
              ×
            </button>
            <h2 id="product-title">{editor.product.name}</h2>
            <p>{editor.product.description}</p>
            {catalog.groups
              .filter((group) => group.productId === editor.product.id)
              .map((group) => (
                <fieldset key={group.id}>
                  <legend>
                    {group.name}{' '}
                    <small>
                      Escolha {group.minQuantity}–{group.maxQuantity}
                    </small>
                  </legend>
                  {catalog.options
                    .filter((option) => option.groupId === group.id)
                    .map((option) => (
                      <label className="choice" key={option.id}>
                        <span>
                          {option.name}{' '}
                          {option.additionalPriceCents
                            ? `+ ${money.format(option.additionalPriceCents / 100)}`
                            : ''}
                        </span>
                        <input
                          aria-label={`Quantidade de ${option.name}`}
                          type="number"
                          min="0"
                          max={group.maxQuantity}
                          value={editor.optionQuantities[option.id] ?? 0}
                          onChange={(event) =>
                            setEditor({
                              ...editor,
                              optionQuantities: {
                                ...editor.optionQuantities,
                                [option.id]: Number(event.target.value)
                              }
                            })
                          }
                        />
                      </label>
                    ))}
                </fieldset>
              ))}
            {catalog.removableIngredients.filter(
              (ingredient) => ingredient.productId === editor.product.id
            ).length ? (
              <fieldset>
                <legend>Remover ingredientes</legend>
                {catalog.removableIngredients
                  .filter((ingredient) => ingredient.productId === editor.product.id)
                  .map((ingredient) => (
                    <label className="choice" key={ingredient.id}>
                      <span>Sem {ingredient.name}</span>
                      <input
                        type="checkbox"
                        checked={editor.removedIngredientIds.includes(ingredient.id)}
                        onChange={(event) =>
                          setEditor({
                            ...editor,
                            removedIngredientIds: event.target.checked
                              ? [...editor.removedIngredientIds, ingredient.id]
                              : editor.removedIngredientIds.filter((id) => id !== ingredient.id)
                          })
                        }
                      />
                    </label>
                  ))}
              </fieldset>
            ) : null}
            <label>
              Observação do item
              <textarea
                maxLength={500}
                value={editor.notes}
                onChange={(event) => setEditor({ ...editor, notes: event.target.value })}
              />
            </label>
            <label>
              Quantidade
              <input
                type="number"
                min="1"
                max="99"
                value={editor.quantity}
                onChange={(event) =>
                  setEditor({ ...editor, quantity: Math.max(1, Number(event.target.value)) })
                }
              />
            </label>
            {checkoutError ? (
              <p className="error" role="alert">
                {checkoutError}
              </p>
            ) : null}
            <button className="primary" onClick={saveProduct}>
              Adicionar · {money.format((editor.product.priceCents * editor.quantity) / 100)}
            </button>
          </section>
        </div>
      ) : null}

      {cartOpen ? (
        <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="cart-title">
          <section className="sheet cart-sheet">
            <button className="close" aria-label="Fechar" onClick={() => setCartOpen(false)}>
              ×
            </button>
            <h2 id="cart-title">Seu pedido</h2>
            {cart.map((item) => (
              <article className="cart-line" key={item.id}>
                <div>
                  <strong>
                    {item.quantity}× {item.product.name}
                  </strong>
                  <small>{money.format((cartItemUnitCents(item) * item.quantity) / 100)}</small>
                </div>
                <div>
                  <button onClick={() => openProduct(item.product, item)}>Editar</button>
                  <button
                    onClick={() =>
                      setCart((current) => current.filter((entry) => entry.id !== item.id))
                    }
                  >
                    Remover
                  </button>
                </div>
              </article>
            ))}
            <button className="link-danger" onClick={() => setCart([])}>
              Limpar carrinho
            </button>
            <hr />
            <h3>Como você quer receber?</h3>
            <div className="mode-switch">
              <button
                disabled={!store.pickupEnabled}
                className={mode === 'PICKUP' ? 'active' : ''}
                onClick={() => setMode('PICKUP')}
              >
                Retirada
              </button>
              <button
                disabled={!deliveryAvailable}
                className={mode === 'DELIVERY' ? 'active' : ''}
                onClick={() => setMode('DELIVERY')}
              >
                Entrega
              </button>
            </div>
            {store.deliveryEnabled && areas.length === 0 ? (
              <p className="delivery-unavailable" role="status">
                Entrega temporariamente indisponível para sua região. A retirada continua
                disponível.
              </p>
            ) : null}
            <div className="form-grid">
              <label>
                Nome
                <input
                  required
                  maxLength={120}
                  autoComplete="name"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                />
              </label>
              <label>
                Telefone
                <input
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </label>
              {mode === 'DELIVERY' ? (
                <>
                  <label className="wide">
                    Rua
                    <input
                      required
                      autoComplete="address-line1"
                      value={street}
                      onChange={(event) => setStreet(event.target.value)}
                    />
                  </label>
                  <label>
                    Número
                    <input
                      required
                      value={number}
                      onChange={(event) => setNumber(event.target.value)}
                    />
                  </label>
                  <label>
                    Bairro / área de entrega
                    <select
                      required
                      value={deliveryAreaId}
                      onChange={(event) => setDeliveryAreaId(event.target.value)}
                    >
                      <option value="">Selecione</option>
                      {areas.map((area) => (
                        <option value={area.id} key={area.id}>
                          {area.name} · {money.format(area.feeCents / 100)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Cidade
                    <input
                      required
                      autoComplete="address-level2"
                      value={city}
                      onChange={(event) => setCity(event.target.value)}
                    />
                  </label>
                  <label>
                    Complemento
                    <input
                      value={complement}
                      onChange={(event) => setComplement(event.target.value)}
                    />
                  </label>
                  <label>
                    Referência
                    <input
                      value={reference}
                      onChange={(event) => setReference(event.target.value)}
                    />
                  </label>
                  <div className="wide location-panel">
                    <div className="location-actions">
                      <button type="button" onClick={locate}>
                        Usar localização atual
                      </button>
                      <button type="button" onClick={() => setMapOpen(true)}>
                        Procurar no mapa
                      </button>
                    </div>
                    {locationMessage ? <p role="status">{locationMessage}</p> : null}
                    {mapOpen ? (
                      <>
                        {mapViewport ? (
                          <DeliveryMap
                            center={mapViewport.center}
                            zoom={mapViewport.zoom}
                            {...(location ? { point: location } : {})}
                            provider={store.map.provider}
                            {...(store.map.styleUrl ? { styleUrl: store.map.styleUrl } : {})}
                            onChange={(latitude, longitude) => {
                              setLocation(
                                confirmedLocation(latitude, longitude, new Date().toISOString())
                              );
                              setLocationMessage('Localização da entrega confirmada.');
                            }}
                          />
                        ) : (
                          <p className="map-fallback" role="status">
                            A região do estabelecimento ainda não foi configurada no mapa. Use sua
                            localização atual para abrir o mapa perto de você e ajustar o ponto, ou
                            entre em contato com o estabelecimento. Seu endereço foi preservado.
                          </p>
                        )}
                        {mapViewport ? (
                          <small>
                            Toque no mapa ou arraste o marcador. O ponto complementa o endereço
                            textual acima.
                          </small>
                        ) : null}
                      </>
                    ) : location ? (
                      <p className="location-confirmed">Localização da entrega confirmada.</p>
                    ) : (
                      <p>Confirme o ponto da entrega. O mapa nunca substitui o endereço.</p>
                    )}
                  </div>
                </>
              ) : null}
              <label className="wide">
                Pagamento
                <select
                  value={paymentIntent}
                  onChange={(event) =>
                    setPaymentIntent(event.target.value as CheckoutPayload['paymentIntent'])
                  }
                >
                  <option value="PIX">PIX na {mode === 'DELIVERY' ? 'entrega' : 'retirada'}</option>
                  <option value="CASH">Dinheiro</option>
                  <option value="DEBIT_CARD">
                    Débito na {mode === 'DELIVERY' ? 'entrega' : 'retirada'}
                  </option>
                  <option value="CREDIT_CARD">
                    Crédito na {mode === 'DELIVERY' ? 'entrega' : 'retirada'}
                  </option>
                  <option value="OTHER">Outro</option>
                </select>
              </label>
              {paymentIntent === 'CASH' ? (
                <label className="wide">
                  Troco para quanto?{' '}
                  <input
                    inputMode="decimal"
                    placeholder="Opcional"
                    value={cashChange}
                    onChange={(event) => setCashChange(event.target.value)}
                  />
                </label>
              ) : null}
              <label className="wide">
                Observação geral
                <textarea
                  maxLength={500}
                  value={orderNotes}
                  onChange={(event) => setOrderNotes(event.target.value)}
                />
              </label>
            </div>
            <div className="totals">
              <span>
                Subtotal <strong>{money.format(subtotal / 100)}</strong>
              </span>
              {mode === 'DELIVERY' ? (
                <span>
                  Entrega{' '}
                  <strong>
                    {selectedArea
                      ? money.format(selectedArea.feeCents / 100)
                      : 'Selecione o bairro'}
                  </strong>
                </span>
              ) : null}
              <span>
                Total estimado <strong>{money.format(estimatedTotal / 100)}</strong>
              </span>
              <small>O servidor confere preços, adicionais e taxa antes de aceitar.</small>
            </div>
            {checkoutError ? (
              <p className="error" role="alert">
                {checkoutError}
              </p>
            ) : null}
            <button
              className="primary"
              disabled={submitting || !modeAvailable || !customerName.trim() || !phone.trim()}
              onClick={() => void checkout()}
            >
              {submitting ? 'Enviando com segurança…' : 'Enviar pedido'}
            </button>
          </section>
        </div>
      ) : null}

      {confirmation ? (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmation-title"
        >
          <section className="sheet confirmation">
            <span className="success-mark">✓</span>
            <h2 id="confirmation-title">Pedido #{confirmation.orderNumber} recebido</h2>
            <p>{publicOrderStatusLabel(confirmation.status)}</p>
            <strong>{money.format(confirmation.totalCents / 100)}</strong>
            <p>Estimativa: {confirmation.estimatedMinutes} minutos.</p>
            <small>Esta tela atualiza automaticamente enquanto estiver aberta.</small>
            <button className="primary" onClick={() => setConfirmation(undefined)}>
              Voltar ao cardápio
            </button>
          </section>
        </div>
      ) : null}
    </>
  );
}
